/**
 * OrganizationContext
 *
 * Tracks which organization the current user is "acting as" for billing purposes.
 * When an org member makes a billable request (search, AI chat, etc.) the active
 * organization_id is included in the request body so the backend routes the cost
 * to the org wallet instead of the personal wallet.
 *
 * Rules:
 * - If the user belongs to at least one organization, the first/primary org is set
 *   as active automatically on login.
 * - The user (or admin) can switch the active org via setActiveOrgId().
 * - The selection is persisted in localStorage per user.
 * - activeOrgId is null for individual users with no org membership.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { storage } from '@/utils/storage';

interface OrganizationContextType {
  activeOrgId: string | null;
  setActiveOrgId: (id: string | null) => void;
  /** Organizations the current user belongs to (from the auth user object). */
  userOrganizations: { id: string; name: string; role: string }[];
}

const OrganizationContext = createContext<OrganizationContextType>({
  activeOrgId: null,
  setActiveOrgId: () => {},
  userOrganizations: [],
});

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const storageKey = user ? `active_org_${user.id}` : null;

  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(() => {
    if (!user) return null;
    const key = `active_org_${user.id}`;
    const saved = storage.local.getItem(key);
    if (saved) return saved;
    // Default to the user's primary organization_id, or first in the list
    if (user.organization_id) return user.organization_id;
    if (user.organizations?.length) return user.organizations[0].id;
    return null;
  });

  // Re-initialize when the user changes (login/logout/switch)
  useEffect(() => {
    if (!user) {
      setActiveOrgIdState(null);
      return;
    }
    const key = `active_org_${user.id}`;
    const saved = storage.local.getItem(key);
    if (saved) {
      setActiveOrgIdState(saved);
      return;
    }
    const defaultOrg = user.organization_id ?? user.organizations?.[0]?.id ?? null;
    setActiveOrgIdState(defaultOrg);
  }, [user?.id]);

  const setActiveOrgId = (id: string | null) => {
    setActiveOrgIdState(id);
    if (storageKey) {
      if (id) {
        storage.local.setItem(storageKey, id);
      } else {
        storage.local.removeItem(storageKey);
      }
    }
  };

  const userOrganizations: { id: string; name: string; role: string }[] =
    user?.organizations ?? [];

  return (
    <OrganizationContext.Provider value={{ activeOrgId, setActiveOrgId, userOrganizations }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  return useContext(OrganizationContext);
}
